import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { ScrollTableOnScreenToTop } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { QueryFactory } from '@lmt/shared-types';
import {
  DataBlockTypeDto,
  ImportMergeFieldDto,
  MergeFieldDto,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { ChoosePrefix } from '@lmt/unicorn-shared/app/components/documents/components/ChoosePrefix';
import { MergeFieldGroupDto } from '@lmt/unicorn-shared/app/components/documents/dto/MergeFieldGroupDto';
import {
  GlobalFormsContext,
  useGlobalForms,
} from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import ExportIcon from '@lmt/unicorn-shared/app/icons/docs/export-merge-field.svg?react';
import ImportIcon from '@lmt/unicorn-shared/app/icons/docs/import-merge-field.svg?react';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertArrayToDictionary } from '@lmt/unicorn-shared/app/helpers/api-helper';
import {
  readFileAsExcel,
  triggerDownloadFileDialog,
} from '@lmt/unicorn-shared/app/helpers/file-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import {
  bindMenu,
  bindToggle,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseTranslationResponse } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query'; // optional
import { useDebounce } from 'use-debounce';
import { getMergeFieldsGroups, mergeFieldCodeAsString } from './helpers';
import { toast } from 'react-toastify';
import styles from './MergeFieldList.module.scss';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-24.svg?react';
import EditIcon from '@lmt/unicorn-shared/app/icons/documents-edit.svg?react';
import ViewIcon from '@lmt/unicorn-shared/app/icons/documents-show.svg?react';
import CopyIcon from '@lmt/unicorn-shared/app/icons/documents-copy.svg?react';
import DeleteIcon from '@lmt/unicorn-shared/app/icons/documents-delete.svg?react';
import Logger from 'js-logger';
import {
  DXRG,
  GridBase,
  Table,
  useReactDataGrid,
  VirtualTable,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { isEmpty } from '../../measurements/copy-datablock-field/common';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';
import { useEventCallback } from '@mui/material';
import { useOnClone } from './helpers';

const ImportIcon2: any = ImportIcon;
const ExportIcon2: any = ExportIcon;

const TableKeys: { [dictionary: string]: string } = {
  name: 'name',
  code: 'code',
  buttons: 'buttons',
};

const GridRoot = (props: GridBase.RootProps) => (
  <DXRG.Grid.Root {...props} className={styles.tableContainerRoot} />
);

const RowButtons = (props: {
  row: QueryFactory.MergeFieldDto;
  onDelete: (mergeField: MergeFieldDto) => Promise<void>;
  onClone: (mergeField: MergeFieldDto) => Promise<void>;
  globalForms: GlobalFormsContext;
}) => {
  const { row, onDelete, onClone, globalForms } = props;
  return (
    <div className={styles.buttonsContainer}>
      {row.isDefault ? (
        <ViewIcon
          className={styles.editIcon}
          onClick={() => {
            globalForms.editMergeField(row.id);
          }}
          data-test-id="edit-merge-field-button"
        />
      ) : (
        <EditIcon
          className={styles.editIcon}
          onClick={() => {
            globalForms.editMergeField(row.id);
          }}
          data-test-id="edit-merge-field-button"
        />
      )}

      <CopyIcon
        className={styles.editIcon}
        onClick={() => {
          onClone(row).catch((e) => Logger.error(e));
        }}
        data-test-id="copy-merge-field-button"
      />
      <DeleteIcon
        className={styles.deleteIcon}
        onClick={() => {
          if (!row.isDefault) {
            onDelete(row).catch((e) => Logger.error(e));
          }
        }}
        data-disabled={row.isDefault}
        data-test-id="delete-merge-field-button"
      />
    </div>
  );
};

const StyledCell = (props: Table.DataCellProps) => {
  switch (props.column.name) {
    case TableKeys.name:
      return (
        <DXRG.Table.Cell
          className={styles.cellName}
          title={props.row.name}
          {...props}
        />
      );
    case TableKeys.code:
      return (
        <DXRG.Table.Cell
          className={styles.cellCode}
          title={mergeFieldCodeAsString(props.row)}
          {...props}
        />
      );
    case TableKeys.buttons:
      return <DXRG.Table.Cell className={styles.cellButtons} {...props} />;
    default:
      return <DXRG.Table.Cell {...props} />;
  }
};

type Props = {
  onInsertMergeField: (mergeField: MergeFieldDto) => void;
};
export const MergeFieldList: React.FC<Props> = React.memo(
  function MergeFieldList(props) {
    useReactDataGrid();
    const localizationMessages = useReactDataGridLocalization();

    const modals = useModal();
    const globalForms = useGlobalForms();
    const i18n = useScopedTranslation('documents');
    const i18next = useScopedTranslation('documents.query_builder');
    const mergeFieldsQuery = QueryFactory.MergeFieldQuery.useGetQuery();
    const mergeFields = mergeFieldsQuery.data;
    const queryClient = useQueryClient();

    const [searchText, setSearchText] = useState('');
    const [searchTextDebounced] = useDebounce(searchText, 300);

    const onDelete = useEventCallback(async (mergeField: MergeFieldDto) => {
      if (
        !(await modals.showConfirm({
          text: i18n.t('delete_merge_field_form_text', {
            name: mergeField.name,
          }),
          title: i18n.t('delete_merge_field_form_title'),
        }))
      ) {
        return;
      }

      try {
        await QueryFactory.MergeFieldClient.remove(mergeField.id);
        await mergeFieldsQuery.refetch();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    });

    const dataBlockTypes = QueryFactory.DataBlockTypeQuery.useGetQuery({
      select: convertArrayToDictionary,
    });
    const exportMergeFields = useCallback(
      async (prefix: string) => {
        if (!mergeFields || !dataBlockTypes.data) return;

        const result = await exportToExcelFile(mergeFields, prefix);
        await triggerDownloadFileDialog(new Blob([result]), 'MergeFields.xlsx');
      },
      [mergeFields, dataBlockTypes.data],
    );

    const importExcelFileRef = useRef<HTMLInputElement>(null);
    const [loadingTitle, setLoadingTitle] = useState<string | undefined>(
      undefined,
    );

    const importMergeFields = useCallback(
      async (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (!dataBlockTypes.data) return;
        try {
          setLoadingTitle(i18n.t('import_loading'));
          const mergeFields = await parseExcelFile(
            ev,
            i18n,
            dataBlockTypes.data,
          );
          const hasDefaultPrefix = mergeFields.some(
            (x) => x.prefix?.toUpperCase() === 'X',
          );
          const promises = mergeFields
            .filter((x) => x.prefix?.toUpperCase() !== 'X')
            .map((mergeField) => {
              return QueryFactory.MergeFieldClient.import_(mergeField);
            });
          if (isEmpty(promises)) {
            if (hasDefaultPrefix) {
              toast(
                [
                  i18n.t('x_prefix_fields_import'),
                  i18n.t('no_data_to_import_toast'),
                ].join(' '),
                {
                  type: 'warning',
                },
              );
            } else {
              toast(i18n.t('no_data_to_import_toast'), {
                type: 'warning',
              });
            }
            return;
          }
          await Promise.all(promises);
          await mergeFieldsQuery.refetch();
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.MergeFieldQuery.getSingleQueryKey(
              undefined!,
            ),
          });
          if (hasDefaultPrefix) {
            toast(i18n.t('x_prefix_fields_import'), {
              type: 'warning',
            });
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setLoadingTitle(undefined);
          // to allow choosing the same file again
          importExcelFileRef.current!.value = '';
        }
      },
      [dataBlockTypes.data],
    );
    const rowClick = useCallback((row: MergeFieldDto) => {
      if ('items' in (row as MergeFieldGroupDto)) {
        return;
      }
      if ('expression' in (row as MergeFieldDto)) {
        props.onInsertMergeField(row);
      }
    }, []);

    const groupedFields = useMemo(
      () =>
        getMergeFieldsGroups(
          mergeFields ?? [],
          searchTextDebounced,
          dataBlockTypes.data ?? {},
        ),
      [dataBlockTypes.data, mergeFields, searchTextDebounced],
    );

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [groupedFields]);

    const onClone = useOnClone();

    const tableColumns = useMemo(
      () => [
        {
          name: TableKeys.name,
          getCellValue: (row: QueryFactory.MergeFieldDto) => (
            <div onClick={() => rowClick(row)} className={styles.fieldTitle}>
              {row.name}
            </div>
          ),
        },
        {
          name: TableKeys.code,
          getCellValue: (row: QueryFactory.MergeFieldDto) =>
            mergeFieldCodeAsString(row),
        },
        {
          name: TableKeys.buttons,
          getCellValue: (row: QueryFactory.MergeFieldDto) => (
            <RowButtons
              row={row}
              onDelete={onDelete}
              onClone={async (mergeField: MergeFieldDto) => {
                await onClone(mergeField, {
                  onLoading: () => setLoadingTitle(i18next.t('copy_loading')),
                  onError: () => setLoadingTitle(undefined),
                  onFinish: () => setLoadingTitle(undefined),
                });
              }}
              globalForms={globalForms}
            />
          ),
        },
      ],
      [onDelete, onClone, rowClick, globalForms],
    );

    const tableColumnExtensions = useMemo<VirtualTable.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.name },
        { columnName: TableKeys.code, width: 73 }, // you could test on `MMM` prefix
        { columnName: TableKeys.buttons, width: 70 },
      ],
      [],
    );
    const popupState = usePopupState({
      variant: 'popper',
      popupId: 'merge-field',
    });
    const gridRef = useRef<any>(null);

    return (
      <Loading loading={!!loadingTitle} title={loadingTitle}>
        <div className={styles.wrapper} data-test-id="merge-field-list">
          <div className={styles.header}>
            <span>{i18n.t('fields_header_title').toUpperCase()}</span>
            <div className={'flexRow'}>
              <AppPopper {...bindMenu(popupState)} placement={'bottom'}>
                <ChoosePrefix
                  onClose={(prefix) => {
                    popupState.close();
                    if (prefix) {
                      void exportMergeFields(prefix);
                    }
                  }}
                />
              </AppPopper>
              <div {...bindToggle(popupState)}>
                <ExportIcon2
                  className={clsx(
                    styles.icon,
                    popupState.isOpen
                      ? styles.iconPressed
                      : styles.iconUnpressed,
                  )}
                  testId={'exportFields'}
                  title={i18n.t('export_button_title')}
                />
              </div>
              <ImportIcon2
                className={clsx(styles.icon, styles.iconUnpressed)}
                onClick={() => importExcelFileRef.current!.click()}
                title={i18n.t('import_button_title')}
                testId={'importFields'}
              />
              <input
                type="file"
                id="importExcelFile"
                ref={importExcelFileRef}
                className={'hidden'}
                onChange={importMergeFields}
              />
              <IconButton
                className={styles.icon}
                onClick={globalForms.addMergeField}
                iconComponent={<AddIcon className={styles.addIcon} />}
                testId={'addMergeField'}
                title={i18n.t('add_new_field_button_title')}
              />
            </div>
          </div>
          <div className={styles.search}>
            <SearchInput
              value={searchText}
              placeholder={i18n.t('search_panel_placeholder')}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={styles.container}>
            <DXRG.Grid
              rows={groupedFields}
              columns={tableColumns}
              rootComponent={GridRoot}
              getRowId={(row) => row.id}
            >
              <DXRG.VirtualTable
                height={'auto'}
                messages={localizationMessages}
                columnExtensions={tableColumnExtensions}
                cellComponent={StyledCell}
                ref={gridRef}
                estimatedRowHeight={33} //we use custom cells, so default value (54px) is too big
              />
            </DXRG.Grid>
          </div>
        </div>
      </Loading>
    );
  },
);

async function exportToExcelFile(mergeFields: MergeFieldDto[], prefix: string) {
  return import('exceljs').then(async (excelJs) => {
    const workbook = new excelJs.Workbook();
    const worksheet = workbook.addWorksheet('Sheet');
    worksheet.columns = [
      { header: 'Code', key: 'fullCode', width: 8 },
      { header: 'Name', key: 'name', width: 40 },
      { header: 'Expression', key: 'expression', width: 100 },
    ];

    mergeFields = mergeFields.filter((x) => x.prefix === prefix);
    mergeFields.forEach((mergeField) => {
      (mergeField as any).fullCode = mergeFieldCodeAsString(mergeField);
    });
    worksheet.addRows(mergeFields);

    worksheet.columns.forEach((_column, index) => {
      const cell = worksheet.getCell(1, index + 1);
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'center',
      };
    });
    const resultBuffer = await workbook.xlsx.writeBuffer();
    return resultBuffer;
  });
}

async function parseExcelFile(
  e: React.ChangeEvent<HTMLInputElement>,
  i18n: UseTranslationResponse<'translation', 'documents'>,
  dataBlockTypes: Record<number, DataBlockTypeDto>,
): Promise<ImportMergeFieldDto[]> {
  const file = e.target!.files![0];
  if (!file) {
    return [];
  }
  const values = await readFileAsExcel(file);
  const importedMergeFields: ImportMergeFieldDto[] = [];

  for (let i = 2; i < values.length; i++) {
    const row = values[i] as unknown[];
    try {
      if (!row?.length) continue;
      const typedRow = parseRowFromExcel(i, row, i18n);
      importedMergeFields.push(typedRow);
    } catch (e) {
      throw e;
    }
  }
  return importedMergeFields;
}

function parseRowFromExcel(
  index: number,
  row: unknown[],
  i18n: UseTranslationResponse<'translation', 'documents'>,
): ImportMergeFieldDto {
  const fullCode = (row[1] as string).toString();
  const matchResult = fullCode.match(
    /^(?<prefix>[a-zA-Z]{1,3})(?<code>\d{1,6})$/,
  );
  if (!matchResult) {
    throw new Error(
      i18n.t('errors.wrong_code_format', { code: fullCode, line: index }),
    );
  }

  const prefix = matchResult?.groups?.prefix ?? '';
  const code = parseInt(matchResult?.groups?.code ?? '');

  const typedRow: ImportMergeFieldDto = {
    code: code,
    prefix: prefix?.toString(),
    name: (row[2] as string)?.toString() || '',
    expression: (row[3] as string)?.toString() || '',
  };
  return typedRow;
}
